import React, { useEffect, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import ErrorModal from "../../components/ErrorModal";
import { usePermission } from "../../hooks/usePermission";
import TimesheetEntries from "./TimesheetEntries";
import TimesheetYear from "./TimesheetYear";
import { apiErrorMessage } from "../../utils/timesheets";
import { getViewAs } from "../../utils/viewAs";

const labelClass = "block text-xs font-medium text-gray-600 mb-1";

function initialParams() {
  const params = new URLSearchParams(window.location.search);
  const year = Number(params.get("year"));
  const month = Number(params.get("month"));
  const validMonth = year >= 2000 && month >= 1 && month <= 12;
  return {
    memberId: params.get("member_id") || "",
    view: validMonth ? { year, month } : null,
    tab: params.get("tab") === "year" ? "year" : "entries",
  };
}

/**
 * Pagina "Prestaties": het personeelslid wordt hier gekozen (enkel voor wie dat mag) en geldt voor beide tabs:
 * "Prestaties ingeven" (maand) en "Jaaroverzicht".
 */
export default function TimesheetsPage() {
  const { hasAnyPermission } = usePermission();
  const canPickPerson = hasAnyPermission("timesheets.edit_all", "timesheets.view_all");
  const [initial] = useState(initialParams);

  const [tab, setTab] = useState(initial.tab);
  const [staff, setStaff] = useState([]);
  const [memberId, setMemberId] = useState(initial.memberId);
  const [ownId, setOwnId] = useState("");
  const [ownInternal, setOwnInternal] = useState(false);
  const [linked, setLinked] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const me = await apiFetch("/timesheets/me");
        setLinked(me.linked);
        if (me.linked) {
          setOwnId(me.member.id);
          setOwnInternal(!!me.member.has_internal);
          setMemberId((cur) => cur || me.member.id);
        }
        if (canPickPerson) {
          const list = await apiFetch("/timesheets/staff");
          setStaff(list.filter((s) => s.is_active));
          if (!me.linked && list.length) setMemberId((cur) => cur || list[0].id);
        }
      } catch (err) {
        setError(apiErrorMessage(err));
      }
    })();
  }, [canPickPerson]);

  const selected = staff.find((m) => m.id === memberId);
  const isInternal = selected ? !!selected.has_internal : memberId === ownId && ownInternal;

  const tabClass = (name) =>
    `px-4 py-2 text-sm font-medium border-b-2 ${
      tab === name ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700"
    }`;

  if (linked === false && !canPickPerson) {
    return (
      <div className="p-4 sm:p-6 max-w-full">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Prestaties</h1>
        <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {getViewAs()
            ? "Je bekijkt de applicatie met de rechten van een andere gebruiker, maar prestaties horen bij jezelf. Jouw eigen login is niet gekoppeld aan een personeelslid, dus er is hier niets te tonen. De prestaties van die gebruiker zie je niet."
            : "Je login is nog niet gekoppeld aan een personeelslid. Vraag een beheerder om dit in te stellen."}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-full">
      {error && <ErrorModal message={error} onClose={() => setError("")} />}
      <h1 className="text-2xl font-bold text-gray-800 mb-4">{canPickPerson ? "Prestaties" : "Mijn prestaties"}</h1>

      {canPickPerson && (
        <div className="mb-4">
          <label className={labelClass} htmlFor="timesheet-member">Personeelslid</label>
          <select
            id="timesheet-member"
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            className="w-64 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {staff.map((s) => (
              <option key={s.id} value={s.id}>{s.naam}</option>
            ))}
          </select>
        </div>
      )}

      <div className="flex border-b border-gray-200 mb-4" role="tablist">
        <button role="tab" aria-selected={tab === "entries"} className={tabClass("entries")} onClick={() => setTab("entries")}>
          Prestaties ingeven
        </button>
        <button role="tab" aria-selected={tab === "year"} className={tabClass("year")} onClick={() => setTab("year")}>
          Jaaroverzicht
        </button>
      </div>

      {memberId && tab === "entries" && (
        <TimesheetEntries memberId={memberId} ownId={ownId} isInternal={isInternal} staff={staff} initialView={initial.view} />
      )}
      {memberId && tab === "year" && <TimesheetYear memberId={memberId} />}
    </div>
  );
}
